<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  policy: { type: Object, required: true },
  actionPolicyItems: { type: Array, default: () => [] },
  actionInstructions: { type: Object, required: true },
  storeName: { type: String, default: '' },
  catalogResultLimit: { type: Number, default: 10 },
  catalogImagesOnly: { type: Boolean, default: false },
  metaCatalogEnabled: { type: Boolean, default: false },
  metaCatalogId: { type: String, default: '' },
  metaCatalogToken: { type: String, default: '' },
  metaCatalogTokenConfigured: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:storeName',
  'update:catalogResultLimit',
  'update:catalogImagesOnly',
  'update:metaCatalogEnabled',
  'update:metaCatalogId',
  'update:metaCatalogToken',
  'update:policy',
  'update:actionInstructions',
  'save',
]);

const { t } = useI18n();

const updatePolicy = (key, value) =>
  emit('update:policy', { ...props.policy, [key]: value });

const updateActionInstruction = (key, value) =>
  emit('update:actionInstructions', {
    ...props.actionInstructions,
    [key]: value,
  });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col gap-6">
    <div
      class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
    >
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        Integração da loja
      </h3>
      <p class="text-sm text-n-slate-11 mb-6">
        A LumerAI pode consultar pedidos apenas do contato da conversa atual. A
        conexão da loja é gerenciada em Configurações &gt; Integrações.
      </p>

      <div class="p-5 mb-4 bg-n-alpha-1 border border-n-strong rounded-xl">
        <label
          class="block font-medium text-n-slate-12 mb-1"
          for="shopify-store-name"
          >Nome exibido da loja</label
        >
        <input
          id="shopify-store-name"
          :value="storeName"
          type="text"
          maxlength="80"
          placeholder="Ex.: LumeraTec"
          class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          @input="emit('update:storeName', $event.target.value.trim())"
        />
        <p class="text-xs text-n-slate-11 mt-2">
          Este nome aparece para o cliente nas mensagens e no checkout. Se ficar
          vazio, a LumerAI usa “nossa loja”.
        </p>
      </div>

      <div class="p-5 mb-4 bg-n-alpha-1 border border-n-strong rounded-xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h4 class="font-medium text-n-slate-12 m-0">
              Consultar catálogo de produtos
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mt-1">
              Busca produtos por nome, marca, categoria ou modelo.
            </p>
          </div>
          <Switch
            :model-value="props.policy.shopify_catalog_enabled"
            aria-label="Ativar consulta ao catálogo de produtos"
            @update:model-value="
              updatePolicy('shopify_catalog_enabled', $event)
            "
          />
        </div>
        <div v-if="props.policy.shopify_catalog_enabled">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div
              class="p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
            >
              <label
                class="block font-medium text-n-slate-12 mb-1"
                for="shopify-catalog-result-limit"
                >Produtos por consulta</label
              >
              <p class="text-sm text-n-slate-11 m-0 mb-3">
                Máximo de itens que a IA pode apresentar por busca (1 a 10).
              </p>
              <input
                id="shopify-catalog-result-limit"
                :value="catalogResultLimit"
                type="number"
                min="1"
                max="10"
                step="1"
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="
                  emit('update:catalogResultLimit', Number($event.target.value))
                "
              />
            </div>
            <div
              class="flex items-center justify-between gap-4 p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
            >
              <div>
                <h4 class="font-medium text-n-slate-12 m-0">
                  Somente produtos com imagem
                </h4>
                <p class="text-sm text-n-slate-11 m-0 mt-1">
                  Evita recomendar produtos sem uma imagem principal.
                </p>
              </div>
              <Switch
                :model-value="catalogImagesOnly"
                aria-label="Mostrar somente produtos Shopify com imagem"
                @update:model-value="emit('update:catalogImagesOnly', $event)"
              />
            </div>
          </div>
          <div class="mt-4">
            <h4 class="font-medium text-n-slate-12 m-0 mb-1">
              Quando consultar o catálogo
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mb-3">
              Esta instrução é enviada junto da ferramenta de busca de produtos.
            </p>
            <TextArea
              :model-value="props.actionInstructions.shopify_catalog_enabled"
              placeholder="Ex.: Consulte quando o cliente pedir preço, disponibilidade, categoria, marca ou modelo."
              :rows="4"
              :max-length="600"
              @update:model-value="
                updateActionInstruction('shopify_catalog_enabled', $event)
              "
            />
          </div>
          <div
            class="mt-4 p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h4 class="font-medium text-n-slate-12 m-0">
                  Vitrine nativa do WhatsApp (Meta)
                </h4>
                <p class="text-sm text-n-slate-11 m-0 mt-1">
                  Em uma caixa oficial e com produtos sincronizados, envia a
                  lista nativa da Meta. Se ela não estiver pronta ou falhar, a
                  LumerAI mantém os cards da loja.
                </p>
              </div>
              <Switch
                :model-value="metaCatalogEnabled"
                aria-label="Ativar vitrine Meta no WhatsApp"
                @update:model-value="emit('update:metaCatalogEnabled', $event)"
              />
            </div>
            <div v-if="metaCatalogEnabled" class="mt-4">
              <label
                class="block font-medium text-n-slate-12 mt-4 mb-1"
                for="shopify-meta-catalog-id"
                >ID do catálogo Meta</label
              >
              <input
                id="shopify-meta-catalog-id"
                :value="metaCatalogId"
                type="text"
                placeholder="Ex.: 123456789012345"
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="
                  emit('update:metaCatalogId', $event.target.value.trim())
                "
              />
              <label
                class="block font-medium text-n-slate-12 mt-4 mb-1"
                for="shopify-meta-catalog-token"
                >Token de acesso ao catálogo Meta</label
              >
              <input
                id="shopify-meta-catalog-token"
                :value="metaCatalogToken"
                type="password"
                autocomplete="new-password"
                placeholder="Cole um novo token para configurar ou substituir"
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="emit('update:metaCatalogToken', $event.target.value)"
              />
              <p class="text-xs text-n-slate-11 mt-2">
                {{
                  metaCatalogTokenConfigured
                    ? 'Token configurado. Para trocar, cole um novo valor acima.'
                    : 'Use um token do usuário de sistema com a permissão catalog_management.'
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="item in props.actionPolicyItems.filter(
            item => item.key !== 'shopify_catalog_enabled'
          )"
          :key="item.key"
          class="p-5 bg-n-alpha-1 border border-n-strong rounded-xl"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h4 class="font-medium text-n-slate-12 m-0">{{ item.title }}</h4>
              <p class="text-sm text-n-slate-11 m-0 mt-1">
                {{ item.description }}
              </p>
            </div>
            <Switch
              :model-value="props.policy[item.key]"
              :aria-label="`Ativar ${item.title}`"
              @update:model-value="updatePolicy(item.key, $event)"
            />
          </div>
          <template v-if="props.policy[item.key]">
            <p
              v-if="item.key === 'shopify_checkout_enabled'"
              class="mt-4 text-sm text-n-slate-11"
            >
              A LumerAI só gera um link após uma confirmação posterior do
              cliente. Ela nunca recebe cartão, PIX ou dados de pagamento.
            </p>
            <TextArea
              :model-value="props.actionInstructions[item.instructionKey]"
              class="mt-4"
              label="Antes de executar (opcional)"
              :placeholder="item.placeholder"
              :rows="3"
              :max-length="600"
              @update:model-value="
                updateActionInstruction(item.instructionKey, $event)
              "
            />
          </template>
        </div>
      </div>

      <p class="mt-5 text-sm text-n-slate-11">
        Mesmo com uma capacidade ativa, a LumerAI não cria pedidos, altera,
        cancela, reembolsa, confirma pagamento ou modifica endereços. O checkout
        seguro apenas cria um carrinho e encaminha o cliente para a página
        segura da loja.
      </p>
      <div class="flex justify-end mt-4">
        <Button
          :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_STORE_PERMISSIONS')"
          color="blue"
          :is-loading="props.isSaving"
          @click="emit('save')"
        />
      </div>
    </div>
  </div>
</template>
